/**
 * Good Atoms Badge Reveal Ceremony
 *
 * Dramatic reveal animation when a new achievement is unlocked:
 *   • Dark backdrop fades in
 *   • 16 spark particles burst outward at varied angles + colours
 *   • Badge circle pops in with spring bounce
 *   • Counter-rotating dashed rings orbit the badge
 *   • Rare badges get purple glow + shimmer scan-line
 *   • Badge name + description stagger in
 *   • "Fantastisk!" CTA + auto-dismiss after 4.5 s
 */

import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { BadgeDef } from '../../data/badges'
import { useT } from '../../i18n'

// ─── Types ────────────────────────────────────────────────────────────────────

interface BadgeRevealProps {
  badge:     BadgeDef | null
  onDismiss: () => void
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Badges that warrant the rare (purple shimmer) treatment */
const RARE = new Set(['monthly-master', 'two-week-titan', 'path-complete', 'perfect-score'])

/** 16 spark particles — varied angle, distance, colour, size, delay */
const SPARK_COLORS = ['#F59E0B', '#34D399', '#60A5FA', '#F472B6', '#A78BFA', '#FB923C']

const SPARKS = Array.from({ length: 16 }, (_, i) => {
  const angle = (i * 360) / 16
  const rad   = (angle * Math.PI) / 180
  const dist  = 80 + Math.sin(i * 1.7) * 35
  return {
    tx:    Math.cos(rad) * dist,
    ty:    Math.sin(rad) * dist,
    color: SPARK_COLORS[i % SPARK_COLORS.length],
    size:  4 + (i % 3) * 2,        // 4 | 6 | 8 px
    delay: 0.05 * (i % 4),
  }
})

// ─── Component ────────────────────────────────────────────────────────────────

export function BadgeReveal({ badge, onDismiss }: BadgeRevealProps) {
  const { t, lang } = useT()
  const isRare = badge ? RARE.has(badge.id) : false

  // Auto-dismiss after 4.5 s
  useEffect(() => {
    if (!badge) return
    const id = setTimeout(onDismiss, 4500)
    return () => clearTimeout(id)
  }, [badge, onDismiss])

  const glowColor   = isRare ? 'rgba(167,139,250,0.55)' : 'rgba(96,165,250,0.45)'
  const ringColor   = isRare ? 'rgba(167,139,250,0.45)' : 'rgba(96,165,250,0.35)'
  const bgGradient  = isRare
    ? 'radial-gradient(circle, #2D1B69 0%, #1A0D3E 100%)'
    : 'radial-gradient(circle, #1C2B4A 0%, #0D1525 100%)'
  const ctaGradient = isRare
    ? 'linear-gradient(135deg, #7C3AED, #A78BFA)'
    : 'linear-gradient(135deg, #1D4ED8, #60A5FA)'
  const labelColor  = isRare ? '#A78BFA' : '#60A5FA'

  return (
    <AnimatePresence>
      {badge && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={lang === 'en' ? 'Badge unlocked' : 'Merke opplåst'}
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(5, 8, 20, 0.9)', backdropFilter: 'blur(4px)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onDismiss}
        >
          {/* Tap-to-dismiss hint */}
          <motion.p
            className="absolute bottom-10 text-white/35 text-sm font-medium tracking-wide select-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.2 }}
          >
            {lang === 'en' ? 'Tap anywhere to continue' : 'Trykk hvor som helst for å fortsette'}
          </motion.p>

          {/* Main content — stopPropagation prevents mis-dismissal during animation */}
          <div
            className="relative flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Spark particles ───────────────────────────────────────────── */}
            {SPARKS.map((s, i) => (
              <motion.div
                key={i}
                aria-hidden="true"
                className="absolute rounded-full pointer-events-none"
                style={{
                  width:      s.size,
                  height:     s.size,
                  background: s.color,
                  top: '50%', left: '50%',
                  marginTop:  -(s.size / 2),
                  marginLeft: -(s.size / 2),
                }}
                initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                animate={{
                  x:       [0, s.tx * 0.5, s.tx],
                  y:       [0, s.ty * 0.5, s.ty],
                  opacity: [0, 1, 0],
                  scale:   [0, 1.5, 0.5],
                }}
                transition={{
                  duration: 1.0,
                  delay:    0.3 + s.delay,
                  ease:     'easeOut',
                }}
              />
            ))}

            {/* ── Badge circle ──────────────────────────────────────────────── */}
            <motion.div
              className="relative flex items-center justify-center rounded-full"
              style={{
                width:     148,
                height:    148,
                background: bgGradient,
                boxShadow: `0 0 50px ${glowColor}, 0 0 100px ${isRare ? 'rgba(167,139,250,0.15)' : 'rgba(96,165,250,0.1)'}`,
              }}
              initial={{ scale: 0, rotate: -25 }}
              animate={{ scale: [0, 1.2, 0.95, 1.06, 1], rotate: [-25, 6, -3, 1, 0] }}
              transition={{ duration: 0.75, delay: 0.2, ease: 'easeOut' }}
            >
              {/* Rare shimmer scan-line */}
              {isRare && (
                <motion.div
                  className="absolute inset-0 rounded-full overflow-hidden pointer-events-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.0 }}
                >
                  <motion.div
                    className="absolute inset-x-0"
                    style={{
                      height: 3,
                      background:
                        'linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)',
                    }}
                    animate={{ top: ['-5%', '105%'] }}
                    transition={{
                      duration:    1.3,
                      delay:       1.1,
                      repeat:      Infinity,
                      repeatDelay: 2.2,
                      ease:        'linear',
                    }}
                  />
                </motion.div>
              )}

              {/* Badge icon */}
              <motion.span
                aria-hidden="true"
                style={{
                  fontSize:   68,
                  lineHeight: 1,
                  filter:     'drop-shadow(0 0 18px rgba(255,255,255,0.45))',
                }}
                initial={{ opacity: 0, scale: 0.2 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.5, ease: 'backOut' }}
              >
                {badge.icon}
              </motion.span>

              {/* Outer solid ring — rotates forward */}
              <motion.div
                aria-hidden="true"
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{ border: `2px solid ${ringColor}` }}
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
              />

              {/* Dashed ring — counter-rotates */}
              <motion.div
                aria-hidden="true"
                className="absolute pointer-events-none rounded-full"
                style={{
                  inset:        -14,
                  border:       `1.5px dashed ${isRare ? 'rgba(167,139,250,0.3)' : 'rgba(96,165,250,0.25)'}`,
                  borderRadius: '50%',
                }}
                animate={{ rotate: -360 }}
                transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
              />
            </motion.div>

            {/* ── "Sjelden merke" / "Nytt merke" label ─────────────────────── */}
            <motion.p
              className="mt-7 text-xs font-extrabold tracking-[0.18em] uppercase select-none"
              style={{ color: labelColor }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.4 }}
            >
              {isRare
                ? (lang === 'en' ? '✦ Rare Badge ✦' : '✦ Sjelden merke ✦')
                : t('badge.new')}
            </motion.p>

            {/* ── Badge name ────────────────────────────────────────────────── */}
            <motion.h2
              className="mt-2 text-white text-[1.6rem] font-extrabold text-center px-8 leading-tight select-none"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.45 }}
            >
              {t(badge.nameKey)}
            </motion.h2>

            {/* ── Badge description ─────────────────────────────────────────── */}
            <motion.p
              className="mt-3 text-white/55 text-sm text-center px-10 leading-relaxed select-none"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.4 }}
            >
              {t(badge.descKey)}
            </motion.p>

            {/* ── CTA button ───────────────────────────────────────────────── */}
            <motion.button
              className="mt-9 px-10 py-3.5 rounded-full font-extrabold text-sm text-white select-none"
              style={{
                background: ctaGradient,
                boxShadow:  `0 6px 24px ${glowColor}`,
              }}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.65, type: 'spring', stiffness: 280, damping: 18 }}
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.94 }}
              onClick={onDismiss}
            >
              {lang === 'en' ? 'Fantastic!' : 'Fantastisk!'} <span aria-hidden="true">🎉</span>
            </motion.button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
